import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'

import UserService from '../../../services/UserService/UserService'

import { Box, Button, Chip, Grid, Link, Paper, Stack, Typography } from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { ObstructionReportsService } from '../../../services/ObstructionReportsService/ObstructionReportsService'
import { ObstructionReportsRelations } from '../../../models/ObstructionReportsRelations.model'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'
import ExpiryDaysDialog, {
  getExtendedExpiry,
  getReactivatedExpiry,
} from '../../../components/ExpiryDaysDialog/ExpiryDaysDialog.component'
import LightboxImage from '../../../components/LightboxImage/LightboxImage.component'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { ObstructionReportsSectionIds, obstructionReportsSections } from '../shared/ObstructionReportsSections'

const ObstructionReportsDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editObstructionReport])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  // the action is kept after closing so the dialog doesn't switch text during its close animation
  const [expiryDialog, setExpiryDialog] = useState<{ open: boolean; action: 'extend' | 'reactivate' }>({
    open: false,
    action: 'extend',
  })
  const closeExpiryDialog = () => setExpiryDialog((prev) => ({ ...prev, open: false }))

  const obstructionReportsService = useMemo(() => new ObstructionReportsService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Obstruction Report`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: obstructionReport } = useQuery(
    [
      'ObstructionReportsService.get',
      {
        id: Number(id),
        relations: [],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      obstructionReportsService.get({
        id: Number(id),
        relations: [],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load obstructionReport.')
        console.error(err)
      },
    },
  )

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (obstructionReport?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      obstructionReportsService
        .delete(obstructionReport.id)
        .then(() => {
          SnackbarContext.show(
            `Obstruction report archived successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, redirect to the obstructionReport list page
          setTimeout(() => {
            navigate(`/obstruction-reports`)
            queryClient.removeQueries([
              'ObstructionReportsService.get',
              {
                id: Number(id),
                relations: [ObstructionReportsRelations.trail],
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Obstruction report failed to archive: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const handleExpiryConfirm = (days: number) => {
    const { action } = expiryDialog
    if (obstructionReport?.id) {
      setIsSaving(true)
      closeExpiryDialog()
      obstructionReportsService[action](obstructionReport.id, days)
        .then((updated) => {
          SnackbarContext.show(
            `Obstruction report ${action === 'reactivate' ? 'reactivated' : 'extended'}. It now expires ${formatDate(
              updated.expiryDate,
            )}.`,
            'success',
          )
          // refetch this report (and any lists showing it) so the new state is displayed
          return queryClient.invalidateQueries()
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Obstruction report failed to ${action}: ${err}`, 'error')
        })
        .finally(() => setIsSaving(false))
    }
  }

  const sectionById = (sectionId: ObstructionReportsSectionIds) =>
    obstructionReportsSections.find((section) => section.id === sectionId) as SectionDefinition

  return (
    <>
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onDelete={handleDelete}
        title="Confirm Archive"
        content="Are you sure you want to archive this obstruction report?"
      />
      {obstructionReport && (
        <ExpiryDaysDialog
          open={expiryDialog.open}
          {...(expiryDialog.action === 'reactivate'
            ? {
                title: 'Reactivate Report',
                description:
                  'The report will show on the trail pages again. Enter how many days it should stay active, starting today.',
                confirmLabel: 'Reactivate',
                getNewExpiry: (days: number) => getReactivatedExpiry(days),
              }
            : {
                title: 'Extend Expiry',
                description: `Currently expires ${formatDate(
                  obstructionReport.expiryDate,
                )}. Enter how many days to add.`,
                confirmLabel: 'Extend',
                getNewExpiry: (days: number) => getExtendedExpiry(obstructionReport.expiryDate, days),
              })}
          onClose={closeExpiryDialog}
          onConfirm={handleExpiryConfirm}
        />
      )}
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && obstructionReport && !obstructionReport.trail && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">
              Failed to locate the assigned trail system or trail for the requested Obstruction Report.
            </Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && obstructionReport && obstructionReport.trail && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Obstruction Reports',
                          href: '/obstruction-reports',
                        },
                      ]
                      breadcrumbs.push({ label: `Obstruction Report ID: ${obstructionReport.id}` })
                      return breadcrumbs
                    }}
                  />
                  {obstructionReport?.archived && hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={() => setExpiryDialog({ open: true, action: 'reactivate' })}>
                        Reactivate
                      </Button>
                    </Stack>
                  )}
                  {!obstructionReport?.archived && hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={() => setExpiryDialog({ open: true, action: 'extend' })}>
                        Extend
                      </Button>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Archive
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={obstructionReport.obstructionType.name}
                  badges={
                    obstructionReport.archived ? <Chip size="small" label="Archived" variant="outlined" /> : undefined
                  }
                  subtitle={[obstructionReport.region?.code, `ID ${obstructionReport.id}`].filter(Boolean).join(' · ')}
                  meta={[
                    `Reported ${formatDate(obstructionReport.reportedAt)}`,
                    `Expires ${formatDate(obstructionReport.expiryDate)}`,
                  ]}
                />

                {/* no per-section edit buttons: this page has never offered an Edit button */}
                <Grid container spacing={2}>
                  {/* --- Obstruction --------------------------------------------- */}
                  <Grid item zero={12} md={7}>
                    <DetailSection section={sectionById(ObstructionReportsSectionIds.obstruction)}>
                      <DetailFieldGrid>
                        <DetailField label="Obstruction Type">
                          {obstructionReport.obstructionType.name
                            ? `${obstructionReport.obstructionType.symbol ?? ''} ${
                                obstructionReport.obstructionType.name
                              }`.trim()
                            : '-'}
                        </DetailField>
                        <DetailField label="Alert Level">{obstructionReport.alertLevel || '-'}</DetailField>
                        <DetailField label="Custom Obstruction">
                          {obstructionReport.statusDescription || '-'}
                        </DetailField>
                        <DetailField label="Reporter Email">
                          {obstructionReport.reporterEmail ? (
                            <Link component="a" href={`mailto:${obstructionReport.reporterEmail}`}>
                              {obstructionReport.reporterEmail}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                      <DetailField label="Description">{obstructionReport.obstructionDescription || '-'}</DetailField>
                      <DetailField label="Image">
                        {obstructionReport.imageUrl ? (
                          <LightboxImage
                            src={obstructionReport.imageUrl}
                            alt="Obstruction"
                            sx={{ display: 'block', maxWidth: '100%', maxHeight: 400, borderRadius: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              py: 6,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="body2" color="grey.500">
                              No Image Available
                            </Typography>
                          </Box>
                        )}
                      </DetailField>
                    </DetailSection>
                  </Grid>

                  {/* --- Location ------------------------------------------------ */}
                  <Grid item zero={12} md={5}>
                    <DetailSection section={sectionById(ObstructionReportsSectionIds.location)}>
                      <DetailField label="Trail System">
                        {obstructionReport.system?.id ? (
                          <Link href={`/trail-systems/${obstructionReport.system.id}`} underline="hover">
                            {obstructionReport.system.name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                      <DetailField label="Trail">
                        {obstructionReport.trail.id ? (
                          <Link href={`/trails/${obstructionReport.trail.id}`} underline="hover">
                            {obstructionReport.trail.name}
                          </Link>
                        ) : (
                          obstructionReport.trail.name || '-'
                        )}
                      </DetailField>
                      <DetailField label="GPS">
                        {obstructionReport.gpsLat && obstructionReport.gpsLong ? (
                          <>
                            {obstructionReport.gpsLat}, {obstructionReport.gpsLong}
                            <br />
                            <Link
                              component="a"
                              href={`https://www.google.com/maps/search/?api=1&query=${obstructionReport.gpsLat},${obstructionReport.gpsLong}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Google Maps
                            </Link>
                            {' · '}
                            {/* mlat/mlon drop the marker; #map centers on it. layers=Y is the CyclOSM style, which shows trails clearly. */}
                            <Link
                              component="a"
                              href={`https://www.openstreetmap.org/?mlat=${obstructionReport.gpsLat}&mlon=${obstructionReport.gpsLong}&layers=Y#map=17/${obstructionReport.gpsLat}/${obstructionReport.gpsLong}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              OpenStreetMap
                            </Link>
                          </>
                        ) : (
                          '-'
                        )}
                      </DetailField>
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ObstructionReportsDetail
